import findOrientation from 'exif-orientation';
import {
  allPass,
  always,
  complement,
  cond,
  equals,
  evolve,
  filter,
  identity,
  is,
  isEmpty,
  map,
  not,
  path,
  pathOr,
  pipe,
  pluck,
  prop,
  propEq,
  reject,
  replace,
  T,
  uniq,
} from 'ramda';
import { contained } from 'ramda-adjunct';
import { createSelector } from 'reselect';
import { FileType } from '../../constants/FileType';
import { MediaType, NON_SECURE_TO_SECURE_MEDIA_TYPES_MAP } from '../../constants/MediaType';
import { NameSpace } from '../../constants/NameSpace';
import { UploadError } from '../../constants/UploadError';
import { getFileTypeFromMime } from '../files';
import { toSecureSiteBasedMediaUrl, toSecureUserBasedMediaUrl } from '../hoc/withMMGRSettings/utils';
import { UploadStatus } from '../hooks/useFilesUploader/types';
import {
  MediaManagerSDKAnyItem,
  MediaManagerSDKAnyUploadingFile,
  MediaManagerSDKFile,
  MediaManagerSDKUploadingFile,
  MediaManagerSDKUploadingFileError,
  MediaManagerSDKUploadingFileErrorInfo,
  MediaManagerSDKUploadingFileSource,
} from '../media-manager-sdk';
import {
  getWixSiteMediaTokenFromFolderId,
  isAllWixMediaRootFolder,
  isBlob,
  isFile,
  isPictureType,
} from '../media-manager-sdk/items';
import { isWixSite } from '../media-manager-sdk/sources';
import { memoize } from '../memoize';
import {
  isArtStoreNamespace,
  isDigitalProductNamespace,
  isWixMusicNamespace,
  isWixVideoNamespace,
  isPortfolioNamespace,
} from '../mmgrSettings/namespace';
import { deserializeStringToArray } from '../string';
import { IOrientation } from './types';

type TIsUploadingFileOfStatus = (uploadingFile: MediaManagerSDKUploadingFile) => boolean;
const isStatusEqual = propEq('status');

export const isFileAborted: TIsUploadingFileOfStatus = (file) => file.isAborted();
export const isFileWaiting = isStatusEqual(UploadStatus.Waiting);
export const isFileUploaded = isStatusEqual(UploadStatus.Succeeded);
export const isFileInProgress = isStatusEqual(UploadStatus.Uploading);
export const isFileProcessing = isStatusEqual(UploadStatus.Processing);
export const isFileFailed = allPass([isStatusEqual(UploadStatus.Failed), complement(isFileAborted)]);
export const isFileAbortable: TIsUploadingFileOfStatus = (uploadingFile) => uploadingFile.canAbort();
export const isFileGotServerErrorWithAutomaticResume: TIsUploadingFileOfStatus = (uploadingFile) =>
  uploadingFile.isTus &&
  uploadingFile.status === UploadStatus.Failed &&
  uploadingFile.error?.data?.error_info?.key === 'files-manager.upload-tab-body-stopped';
export const isNetworkErrorResumableFiles: TIsUploadingFileOfStatus = (uploadingFile) => {
  return (
    uploadingFile.isTus &&
    uploadingFile.status === UploadStatus.Failed &&
    uploadingFile.error?.data?.error_info?.key === 'files-manager.upload-tab-body-connectivity'
  );
};
export const isFileInvalid: TIsUploadingFileOfStatus = (uploadingFile) =>
  uploadingFile.error instanceof Error && !uploadingFile.isAborted();
export const getUploadedSize = prop<MediaManagerSDKUploadingFile, 'bytesLoaded'>('bytesLoaded');

export const isUploadingSourceFile = (item: MediaManagerSDKAnyItem | File) => is(File, item) || isFile(item);

export const getErrorInfo = pipe<
  MediaManagerSDKUploadingFileError,
  MediaManagerSDKUploadingFileErrorInfo,
  MediaManagerSDKUploadingFileErrorInfo
>(
  pathOr({ key: UploadError.General, args: [], message: '' }, ['data', 'error_info']),
  evolve({
    key: replace(/wpm_error/g, 'wmp_error'),
  }),
);

export const getItemFromServerResponse = <T extends MediaManagerSDKAnyItem>(
  uploadingFile: MediaManagerSDKUploadingFile,
): T => pathOr<T, T>({} as T, ['serverResponse', 'data'], uploadingFile);

export const getUploadingFileMediaType: (uploadingFile: MediaManagerSDKUploadingFile) => MediaType = path([
  'source',
  'mediaType',
]);

export const getUploadingFileSourceFile: (
  uploadingFile: MediaManagerSDKAnyUploadingFile,
) => MediaManagerSDKUploadingFileSource['file'] = path(['source', 'file']);

export const getUploadedFilesMediaTypes = pipe<
  MediaManagerSDKUploadingFile[],
  MediaManagerSDKFile[],
  MediaType[],
  MediaType[]
>(map<MediaManagerSDKUploadingFile, MediaManagerSDKFile>(getItemFromServerResponse), pluck('mediaType'), uniq);

export const getFailedFiles = createSelector<
  MediaManagerSDKUploadingFile[],
  MediaManagerSDKUploadingFile[],
  MediaManagerSDKUploadingFile[]
>([identity], filter(isFileFailed));
export const getUploadedFiles = createSelector<
  MediaManagerSDKUploadingFile[],
  MediaManagerSDKUploadingFile[],
  MediaManagerSDKUploadingFile[]
>([identity], filter(isFileUploaded));
export const getAbortedFiles = createSelector<
  MediaManagerSDKUploadingFile[],
  MediaManagerSDKUploadingFile[],
  MediaManagerSDKUploadingFile[]
>([identity], filter(isFileAborted));
export const getNotAbortedFiles = createSelector<
  MediaManagerSDKUploadingFile[],
  MediaManagerSDKUploadingFile[],
  MediaManagerSDKUploadingFile[]
>([identity], reject(isFileAborted));
export const getInvalidFiles = createSelector<
  MediaManagerSDKUploadingFile[],
  MediaManagerSDKUploadingFile[],
  MediaManagerSDKUploadingFile[]
>([identity], filter(isFileInvalid));
export const getAbortableFiles = createSelector<
  MediaManagerSDKUploadingFile[],
  MediaManagerSDKUploadingFile[],
  MediaManagerSDKUploadingFile[]
>([identity], filter(isFileAbortable));
export const getNetworkErrorResumableFiles = createSelector<
  MediaManagerSDKUploadingFile[],
  MediaManagerSDKUploadingFile[],
  MediaManagerSDKUploadingFile[]
>([identity], filter(isNetworkErrorResumableFiles));
export const getSeverErrorAutomaticResumeFiles = createSelector<
  MediaManagerSDKUploadingFile[],
  MediaManagerSDKUploadingFile[],
  MediaManagerSDKUploadingFile[]
>([identity], filter(isFileGotServerErrorWithAutomaticResume));

export const areSomeMediaTypesUnselected = (
  uploadingFiles: MediaManagerSDKUploadingFile[],
  mediaType: string,
): boolean => {
  if (!mediaType) {
    return true;
  }

  const mediaTypeArray = deserializeStringToArray<MediaType>(mediaType);

  return pipe<
    MediaManagerSDKUploadingFile[],
    MediaManagerSDKUploadingFile[],
    MediaType[],
    MediaType[],
    boolean,
    boolean
  >(
    getUploadedFiles,
    getUploadedFilesMediaTypes,
    reject(contained(mediaTypeArray)),
    isEmpty,
    not,
  )(uploadingFiles);
};

type TIsFileType = (fileType: FileType) => boolean;

export const isImageFileType: TIsFileType = equals(FileType.Image);
export const isVideoFileType: TIsFileType = equals(FileType.Video);
export const isAudioFileType: TIsFileType = equals(FileType.Audio);
export const isZipFileType: TIsFileType = equals(FileType.Zip);
export const isVectorArtFileType: TIsFileType = equals(FileType.VectorArt);
export const isSWFFile: TIsFileType = equals(FileType.SWF);

const getNonSecureMediaTypeByFileType: (fileType: FileType) => MediaType = cond([
  [isVectorArtFileType, always(MediaType.VectorArt)],
  [isSWFFile, always(MediaType.SWF)],
  [isImageFileType, always(MediaType.Picture)],
  [isVideoFileType, always(MediaType.Video)],
  [isAudioFileType, always(MediaType.Music)],
  [isZipFileType, always(MediaType.Zip)],
  [T, always(MediaType.Document)],
]);

const getPortfolioMediaTypes: (mediaType: MediaType) => MediaType = cond([
  [equals(MediaType.Video), always(MediaType.Video)],
  [T, always(MediaType.SecurePicture)],
]);

export const getSecureMediaType: (mediaType: MediaType, namespace: NameSpace) => MediaType = (mediaType, namespace) => {
  return cond([
    [isWixMusicNamespace, always(MediaType.SecureMusic)],
    [isWixVideoNamespace, always(MediaType.SecureVideo)],
    [isArtStoreNamespace, always(MediaType.SecurePicture)],
    [isPortfolioNamespace, always(getPortfolioMediaTypes(mediaType))],
    [isDigitalProductNamespace, always(NON_SECURE_TO_SECURE_MEDIA_TYPES_MAP[mediaType])],
  ])(namespace);
};

export const defineRealFileMediaType = pipe(getFileTypeFromMime, getNonSecureMediaTypeByFileType);

export const defineMediaType: (
  mime: string,
  isSecureContext: boolean,
  namespace?: NameSpace,
) => MediaType | undefined = (mime, isSecureContext, namespace = NameSpace.None) => {
  if (!isSecureContext) {
    return;
  }

  const fileType = getFileTypeFromMime(mime);
  const mediaType = getNonSecureMediaTypeByFileType(fileType);

  return getSecureMediaType(mediaType, namespace) || mediaType;
};

export const createObjectUrl: (file: File) => string = memoize((file) => URL.createObjectURL(file));

export const shouldHaveThumbnailSrc = (uploadingFile: MediaManagerSDKAnyUploadingFile): boolean => {
  const { file: sourceFile, mediaType } = uploadingFile.source;

  return (
    isFile(sourceFile as MediaManagerSDKAnyItem) &&
    contained(
      [MediaType.Picture, MediaType.SecurePicture, MediaType.VectorArt, MediaType.Video, MediaType.SecureVideo],
      mediaType,
    )
  );
};

export const defaultOrientation: IOrientation = {
  scale: {
    x: 1,
    y: 1,
  },
  rotate: 0,
  exif: 0,
};

type TGetOrientation = (file: File) => Promise<IOrientation>;
export const getOrientation: TGetOrientation = memoize(
  (file) =>
    new Promise<IOrientation>((resolve) => {
      if (!is(File, file)) {
        resolve(defaultOrientation);
        return;
      }

      findOrientation(file, (err, orientation) => {
        if (err) {
          resolve(defaultOrientation);
        } else {
          resolve(orientation);
        }
      });
    }),
);

export const getUploadingFileProps = ({ uploadingFile, isSecureContext, privateMediaRestAPI }) => {
  const sourceFile = getUploadingFileSourceFile(uploadingFile);
  const isRealFile = isBlob(sourceFile);
  const mediaType = isRealFile
    ? (uploadingFile as MediaManagerSDKUploadingFile).options.realFileMediaType
    : isFile(sourceFile as MediaManagerSDKAnyItem)
    ? (sourceFile as MediaManagerSDKFile).mediaType
    : MediaType.Folder;
  let src = null;

  if (isRealFile && isPictureType(mediaType)) {
    src = createObjectUrl(sourceFile as File);
  } else if (shouldHaveThumbnailSrc(uploadingFile)) {
    const { previewUrl, source, folderId } = sourceFile as MediaManagerSDKFile;

    src = previewUrl;

    if (isSecureContext && isWixSite(source)) {
      const allWixMediaRootFolderSrc = replace(
        toSecureSiteBasedMediaUrl(privateMediaRestAPI),
        toSecureUserBasedMediaUrl(privateMediaRestAPI),
        src,
      );
      const siteFolderSrc = `${src}?site_token=${getWixSiteMediaTokenFromFolderId(folderId)}`;

      src = isAllWixMediaRootFolder(folderId) ? allWixMediaRootFolderSrc : siteFolderSrc;
    }
  }

  return {
    src,
    file: isBlob(sourceFile) ? sourceFile : null,
    mediaType,
  };
};
